import { A } from "@solidjs/router"
import { createQuery } from "@urql/solid"
import { createMemo, createSignal, For, Show } from "solid-js"
import { createSession } from "@/context/session-provider"
import { Icon } from "@/designSystem/components/icon"
import { Mini } from "@/designSystem/components/mini/mini"
import { Text } from "@/designSystem/components/text"
import { MainLayout } from "@/designSystem/layouts/main-layout"
import { NavigationWithButtons } from "@/designSystem/patterns/navigation"
import {
  createFragment,
  type FragmentOf,
  graphql,
  readFragment,
} from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import { useRelativeTime } from "@/lib/use-relative-time"
import {
  ThreadParticipants,
  ThreadParticipantsFragment,
} from "./_thread-participants"
import {
  containerClass,
  emptyStateClass,
  sectionClass,
  sectionTitleClass,
  tabsRowClass,
  threadCardBodyClass,
  threadCardClass,
  threadCardMainClass,
  threadCardUnreadClass,
  threadContentClass,
  threadDateClass,
  threadDirectionIconClass,
  threadHeaderClass,
  threadLastMessageClass,
  threadListClass,
  threadMetaClass,
  threadPostReferenceLabelClass,
  threadPostReferenceTitleClass,
  threadPrivateBadgeClass,
  threadPublicationNameClass,
  threadSubjectClass,
  threadTypeIconColumnClass,
} from "./inbox-list.css"

export const ThreadCardFragment = graphql(
  `fragment ThreadCardFragment on Thread {
    id
    subject
    private
    unreadCount
    post {
      id
      title
      publication {
        id
        name
      }
    }
    lastMessage {
      id
      content
      createdAt
      author {
        id
      }
    }
    ...ThreadParticipantsFragment
  }`,
  [ThreadParticipantsFragment],
)

const InboxQuery = graphql(
  `query Inbox($limit: Int, $offset: Int, $private: Boolean) {
    inbox(limit: $limit, offset: $offset, private: $private) {
      id
      unreadCount
      ...ThreadCardFragment
    }
  }`,
  [ThreadCardFragment],
)

type TabType = "all" | "emails" | "comments"

export function InboxList() {
  const { LL } = useI18nContext()
  const [activeTab, setActiveTab] = createSignal<TabType>("all")

  const [result] = createQuery({
    query: InboxQuery,
    variables: () => ({
      limit: 50,
      offset: 0,
      private: activeTab() === "all" ? undefined : activeTab() === "emails",
    }),
    requestPolicy: "cache-and-network",
  })

  const unreadThreads = createMemo(
    () => result.data?.inbox?.filter(t => t.unreadCount > 0) ?? [],
  )

  const readThreads = createMemo(
    () => result.data?.inbox?.filter(t => t.unreadCount === 0) ?? [],
  )

  return (
    <MainLayout
      hue="rose"
      header={<NavigationWithButtons title={LL().inbox.title()} hue="rose" />}
      query={result}
    >
      {data => (
        <div class={containerClass}>
          <div class={tabsRowClass}>
            <Mini.Menu>
              <Mini.Section>
                <Mini.ToggleGroup
                  value={activeTab()}
                  onChange={v => v && setActiveTab(v as TabType)}
                  values={[
                    { value: "all", title: LL().inbox.tabAll() },
                    {
                      value: "emails",
                      title: LL().inbox.tabEmails(),
                      labelIconName: "mail-open",
                    },
                    {
                      value: "comments",
                      title: LL().inbox.tabComments(),
                      labelIconName: "message-circle",
                    },
                  ]}
                />
              </Mini.Section>
            </Mini.Menu>
          </div>

          <Show
            when={data.inbox && data.inbox.length > 0}
            fallback={
              <div class={emptyStateClass}>
                <Icon
                  name={
                    activeTab() === "comments" ? "message-circle" : "mail-open"
                  }
                  size="32"
                  color="sand300"
                />
                <Text
                  color="sand700"
                  fontSize="l"
                  fontWeight="600"
                  textAlign="center"
                >
                  {activeTab() === "emails"
                    ? LL().inbox.emptyLetters()
                    : activeTab() === "comments"
                      ? LL().inbox.emptyComments()
                      : LL().inbox.empty()}
                </Text>
                <Text color="sand500" fontSize="s" textAlign="center">
                  {LL().inbox.emptyDescription()}
                </Text>
              </div>
            }
          >
            <Show when={unreadThreads().length > 0}>
              <div class={sectionClass}>
                <div class={sectionTitleClass}>{LL().inbox.unread()}</div>
                <div class={threadListClass}>
                  <For each={unreadThreads()}>
                    {thread => <ThreadCard thread={thread} unread />}
                  </For>
                </div>
              </div>
            </Show>

            <Show when={readThreads().length > 0}>
              <div class={sectionClass}>
                <div class={sectionTitleClass}>{LL().inbox.read()}</div>
                <div class={threadListClass}>
                  <For each={readThreads()}>
                    {thread => <ThreadCard thread={thread} />}
                  </For>
                </div>
              </div>
            </Show>
          </Show>
        </div>
      )}
    </MainLayout>
  )
}

function ThreadCard(props: {
  thread: FragmentOf<typeof ThreadCardFragment>
  unread?: boolean
}) {
  const { LL } = useI18nContext()
  const { session } = createSession()
  const thread = createFragment(ThreadCardFragment, () => props.thread)

  const lastMessageDate = useRelativeTime(
    () => thread().lastMessage?.createdAt ?? 0,
  )

  const lastMessagePreview = createMemo(() => {
    const msg = thread().lastMessage
    if (!msg) return ""
    try {
      const parsed = JSON.parse(msg.content)
      return (
        parsed.content
          ?.map(
            (node: any) =>
              node.content?.map((inline: any) => inline.text ?? "").join("") ??
              "",
          )
          .join(" ")
          .trim() ?? ""
      )
    } catch {
      return msg.content
    }
  })

  return (
    <A
      href={`/inbox/${thread().id}`}
      class={`${threadCardClass}${props.unread ? ` ${threadCardUnreadClass}` : ""}`}
    >
      <Show when={thread().private}>
        <div class={threadPrivateBadgeClass}>Private</div>
      </Show>
      <div class={threadCardBodyClass}>
        <Icon
          name={thread().private ? "mail-open" : "message-circle"}
          size="18"
          color={thread().private ? "rose500" : "sand400"}
          class={threadTypeIconColumnClass}
        />
        <div class={threadCardMainClass}>
          <div class={threadHeaderClass}>
            <ThreadParticipants
              participants={
                readFragment(ThreadParticipantsFragment, thread()).participants
              }
            />
          </div>
          <div class={threadContentClass}>
            <Show when={thread().subject}>
              {subject => <div class={threadSubjectClass}>{subject()}</div>}
            </Show>
            <Show when={lastMessagePreview()}>
              {preview => <div class={threadLastMessageClass}>{preview()}</div>}
            </Show>
          </div>
          <div class={threadMetaClass}>
            <Show when={thread().lastMessage?.createdAt}>
              <div class={threadDateClass}>{lastMessageDate()}</div>
            </Show>
            <Show when={thread().post} keyed>
              {post => (
                <>
                  <span class={threadPostReferenceLabelClass}>
                    {LL().inbox.inReplyTo()}
                  </span>
                  <span class={threadPostReferenceTitleClass}>
                    {post.title}
                  </span>
                  <Show
                    when={
                      post.publication?.id &&
                      post.publication.id !== session()?.user?.publication?.id
                    }
                  >
                    <span class={threadPublicationNameClass}>
                      · {post.publication?.name}
                    </span>
                  </Show>
                </>
              )}
            </Show>
          </div>
        </div>
        <Show when={thread().post} keyed>
          {post => (
            <Icon
              name={
                post.publication?.id &&
                post.publication.id !== session()?.user?.publication?.id
                  ? "arrow-right"
                  : "arrow-left"
              }
              size="18"
              color="sand300"
              class={threadDirectionIconClass}
            />
          )}
        </Show>
      </div>
    </A>
  )
}
